import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import { openModal } from "../../reducers/modalReducer";
import { Input, InputProps } from "@almafintech/react-components/Input";
import { InputSelect } from "@almafintech/react-components/InputSelect";
import { InputPhone } from "@almafintech/react-components/InputPhone";
import { FileData, InputFile } from "@almafintech/react-components/InputFile";
import { InputTextArea } from "@almafintech/react-components/InputTextArea";
import { InputAddress } from "@almafintech/react-components/InputAddress";
import { RadioGroup } from "@almafintech/react-components/RadioGroup";
import { Tooltip } from "@almafintech/react-components/Tooltip";
import { NewDatePicker } from "@almafintech/react-components/NewDatePicker";
import { Checkbox } from "@almafintech/react-components/Checkbox";
import { Button } from "@almafintech/react-components/Button";
import { Message } from "@almafintech/react-components/Message";
import {
  ConditionalBool,
  DocumentMetadata,
  Field,
  SegmentedInputOption,
  Tooltip as TooltipType,
  UploadedFile,
  SerializableFile,
  ConditionalTooltip,
  InputType,
} from "../../types";
import { FormikProps, FormikValues } from "formik";
import SegmentedOptionsInput from "../SegmentedOptionsInput/SegmentedOptionsInput";
import useWindowWidth from "../../hooks/useWindowWidth";
import {
  handleDateInputKeyDown,
  handleDateFieldValueChanged,
  isEmptyFile,
  minFileSize,
} from "../../utils";
import {
  getIsConditionallyTrue,
  getNestedValue,
  isSchemaRequired,
} from "./utils";
import tooltipIcon from "../../assets/images/icons/ui/tooltip.svg";
import { parseDate, CalendarDate, DateValue } from "@internationalized/date";
import {
  base64ToFile,
  calendarDateToString,
  stringToCalendarDate,
} from "../../app/utils";
import { RootState } from "../../app/store";
import { fileDragTip, inputFile, optionalLabelSuffix } from "../../i18n/inputs";
import { CheckboxGroup } from "@nextui-org/react";

import styles from "./TemplateInterpreter.module.scss";

interface ColumnsWrapperProps {
  id?: string;
  columns: number | undefined;
  children: React.ReactNode;
  className?: string;
}

interface TemplateInputSelectorProps {
  currentStepFields?: Field[];
  field: Field;
  formik: FormikProps<FormikValues>;
}

const ColumnsWrapper = ({
  id,
  columns,
  children,
  className,
}: ColumnsWrapperProps) => {
  const { columnsWrapper, twoColumn, threeColumn } = styles;

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const columnsClassNames = [];

  if (!isMobile && columns) {
    columnsClassNames.push(
      columns === 2 ? twoColumn : columns === 3 ? threeColumn : ""
    );
  }

  return (
    <div
      id={id}
      className={`${columnsWrapper} ${columnsClassNames.join(" ")} ${
        className || ""
      }`}
    >
      {children}
    </div>
  );
};

interface ConditionalResetAndScrollTo {
  conditionalBoolCurrentField: ConditionalBool[] | undefined;
  boolFieldsWithSameConditionalField: ConditionalBool[] | undefined;
  values: FormikValues;
  setFieldValue: (field: string, value: unknown) => void;
  scrollToElementName: (name: string, inputType: InputType) => void;
}

const conditionalResetAndScrollTo = ({
  conditionalBoolCurrentField,
  boolFieldsWithSameConditionalField,
  values,
  setFieldValue,
  scrollToElementName,
}: ConditionalResetAndScrollTo) => {
  // Scroll to conditional input to be visible
  if (conditionalBoolCurrentField && conditionalBoolCurrentField.length > 0) {
    setTimeout(() => {
      // If the current field triggers another conditional field
      for (const field of conditionalBoolCurrentField) {
        // If conditionalField have multiple boolFields, reset it only if all of them have no value
        if (
          Array.isArray(boolFieldsWithSameConditionalField) &&
          boolFieldsWithSameConditionalField?.length > 0
        ) {
          const allBoolFieldsHaveNoValue = [
            ...conditionalBoolCurrentField,
            ...boolFieldsWithSameConditionalField,
          ].every(
            (conditional) =>
              !getNestedValue(values, conditional.boolField as string)
          );

          if (allBoolFieldsHaveNoValue) {
            for (const field of boolFieldsWithSameConditionalField) {
              setFieldValue(field?.conditionalField, "");
            }
          }
        } else {
          // The conditional file is emptied
          setFieldValue(field?.conditionalField, "");
        }
      }

      if (!conditionalBoolCurrentField[0]?.dontScrollTo) {
        const { conditionalField, inputType } = conditionalBoolCurrentField[0];
        scrollToElementName(conditionalField, inputType);
      }
    }, 200);
  }
};

const scrollToElementName = (name: string, inputType?: InputType) => {
  const element =
    inputType === "file"
      ? document.getElementById(`inputFile-container-${name}`)
      : inputType === "select"
      ? document.getElementById(name)
      : document.querySelector(`[name="${name}"]`);

  element && element.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Return File from metadata
const fetchFileFromMetadata = async (meta: DocumentMetadata) => {
  const response = await fetch(meta.publicUrl);
  const blob = await response.blob();
  const file: UploadedFile = new File([blob], meta.originalFileName, {
    type: meta.contentType,
  });
  // Mark this file as already uploaded so we don't re-upload it
  file.__alreadyUploaded = true;
  return file;
};

// Compare files using unique IDs
const compareFiles = (file1: File | FileData, file2: File | FileData) => {
  if (!file1 || !file2) return false;

  // For FileData objects with id
  if ("id" in file1 && "id" in file2) {
    return file1.id === file2.id;
  }

  // For File objects, use unique ID generation
  const getFileId = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  return getFileId(file1 as File) === getFileId(file2 as File);
};

// Convert any type to File
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const anyToFile = async (item: any): Promise<File | null> => {
  // If the item is already a File, return it
  if (item instanceof File) return item;

  // If the item was fetched from the backend and is a metadata object
  // fetch the file from the public URL
  // and return it as a File object
  if (item && item.publicUrl) {
    return await fetchFileFromMetadata(item as DocumentMetadata);
  }

  // If the item is a base64 string, convert it to a File object
  // When formik values are saved to Redux during each "Continue" action,
  // File objects are converted to base64 strings because Redux does not support File objects.
  // Here, we convert those base64 strings back into File objects when retrieving the value from Redux.
  if (item && item.base64String) {
    const file: UploadedFile = base64ToFile(
      item.base64String,
      item.name,
      item.type
    );
    file.__alreadyUploaded = true;

    return file;
  }

  return null;
};

const TemplateInputSelector = ({
  currentStepFields,
  field,
  formik,
}: TemplateInputSelectorProps) => {
  const {
    labelInfo,
    optionalLabel,
    file: fileStyle,
    externalTooltip,
    anchorStyle,
    checkboxWrapper,
    checkboxLabel,
    checkboxGroupWrapper,
    multilineSelectListbox,
  } = styles;

  const state = useAppSelector((state: RootState) => state);

  const {
    name,
    label,
    conditionalTooltip,
    anchor,
    tooltip,
    type,
    dynamicType,
    options,
    selectType,
    confirmSelection,
    disabled,
    placeholder,
    initialValue,
    maxSize,
    validTypes,
    isNumberPercentage,
    minDatePickerDate,
    maxDatePickerDate,
    defaultSelectedKeys,
    canUploadMultipleFiles,
    infoText,
    infoTextPosition,
    onChange,
    onBlur,
    showExternalBox,
    conditional,
    conditionalFile,
    allConditionalsTrue,
    conditionalBool,
    columns,
    autoSelect,
    country,
    conditionalValidation,
    validation,
    autoComplete,
    maxLength,
    dynamicOptions,
    disabledByUrlParams,
    showOptionalLabel,
    text,
    buttonProps,
    hideLabel,
    hideField,
    className,
    multilineSelect,
    alertContent,
  } = field;

  const {
    getFieldProps,
    values,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    errors,
    touched,
    handleBlur,
  } = formik;

  const error = getNestedValue(errors, name);

  const touch = getNestedValue(touched, name);

  const value = getNestedValue(values, name);

  const [disabledByUrl, setDisabledByUrl] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Manage uploaded files
  useEffect(() => {
    if (!value) return;

    const processFiles = async () => {
      if (canUploadMultipleFiles && Array.isArray(value)) {
        const files = (
          await Promise.all(
            value.map(async (item) => {
              return await anyToFile(item);
            })
          )
        ).filter(Boolean) as File[];

        // Filter out files that are already uploaded
        const isSame =
          Array.isArray(value) &&
          value.length === files.length &&
          value.every((v, i) => v instanceof File && v === files[i]);

        if (!isSame) {
          setUploadedFiles((prevFiles) => {
            const newFiles = [...prevFiles, ...files];
            setFieldValue(name, newFiles);

            return newFiles;
          });
        }
      } else if (value && !Array.isArray(value)) {
        const file = await anyToFile(value);

        if (!file) return;

        setUploadedFiles([file]);
        setFieldValue(name, file);
      }
    };

    processFiles();
  }, [value, canUploadMultipleFiles, name]);

  const segmentedOptions =
    type === "multiple-radio" && (options as SegmentedInputOption[]);

  const [searchParams] = useSearchParams();

  const getShouldConditionallyRender = () => {
    if (hideField) return false;

    // If the field is shown conditionally by a file
    if (conditionalFile) {
      // If the file that is conditionally needed is present or the field has a value show the field
      return !!getNestedValue(values, conditionalFile.name) || !!value;
    }

    if (conditionalBool) {
      return conditionalBool(values, dispatch, state);
    }

    // If the field is shown conditionally by multiple fields
    if (conditional) {
      return getIsConditionallyTrue(values, conditional, allConditionalsTrue);
    }

    return true;
  };

  const dispatch = useAppDispatch();
  const language = state.securities.language ?? "es";

  const getLabel = () => {
    if (hideLabel) return;

    const isOptional =
      (validation && !isSchemaRequired(validation)) ||
      (conditionalValidation &&
        !isSchemaRequired(conditionalValidation(state)));

    if (
      (type !== "text" || (tooltip?.content && !tooltip.label)) &&
      type !== "dni" &&
      tooltip
    ) {
      return tooltip.label ? (
        <div className={"flex w-full justify-between items-center flex-wrap"}>
          <p>{label}</p>
          {tooltip.content ? (
            <Tooltip
              placement="top"
              content={tooltip.content}
              width={tooltip.width || 300}
            >
              <p className={labelInfo}>{tooltip.label}</p>
            </Tooltip>
          ) : (
            <p
              onClick={() =>
                tooltip.modal && dispatch(openModal({ name: tooltip.modal }))
              }
              className={labelInfo}
            >
              {tooltip.label}
            </p>
          )}
        </div>
      ) : (
        <div
          className={`${optionalLabel} flex w-full gap-2 items-center flex-wrap`}
        >
          <p>{label}</p>
          {isOptional && <span>- {optionalLabelSuffix[language]}</span>}
          <Tooltip
            placement="top"
            content={tooltip.content}
            width={tooltip.width || 300}
            isDisabled={!tooltip.content}
          >
            <img
              src={tooltip.icon || tooltipIcon}
              alt="Tooltip"
              onClick={() =>
                tooltip.modal && dispatch(openModal({ name: tooltip.modal }))
              }
              className="cursor-pointer"
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <div className={optionalLabel}>
        {label}
        {showOptionalLabel && isOptional && (
          <span> - {optionalLabelSuffix[language]}</span>
        )}
      </div>
    );
  };

  const getConditionalTooltip = () => {
    let tooltipToShow: TooltipType | null = null;

    const valuesMatch = (
      value: ConditionalTooltip["value"],
      conditionalValue: string
    ) =>
      Array.isArray(value)
        ? value
            .filter((v): v is string => v !== null)
            .includes(conditionalValue)
        : value === conditionalValue;

    if (Array.isArray(conditionalTooltip)) {
      tooltipToShow =
        conditionalTooltip.find((cond) =>
          valuesMatch(cond.value, getNestedValue(values, cond.name) as string)
        )?.tooltip || null;
    } else if (conditionalTooltip) {
      const conditionalValue = getNestedValue(
        values,
        conditionalTooltip.name
      ) as string;

      if (valuesMatch(conditionalTooltip.value, conditionalValue))
        tooltipToShow = conditionalTooltip.tooltip;
    }

    if (tooltipToShow) return getTooltip(tooltipToShow);

    return undefined;
  };

  const getTooltip = (tooltipToShow: TooltipType) => {
    if (tooltipToShow) {
      return tooltipToShow.label ? (
        <div>
          {tooltipToShow.content ? (
            <Tooltip
              placement="top"
              content={tooltipToShow.content}
              width={300}
            >
              <p className={labelInfo}>{tooltipToShow.label}</p>
            </Tooltip>
          ) : (
            <p
              onClick={() =>
                dispatch(openModal({ name: tooltipToShow?.modal }))
              }
              className={labelInfo}
            >
              {tooltipToShow.label}
            </p>
          )}
        </div>
      ) : (
        <Tooltip placement="top" content={tooltipToShow.content} width={300}>
          <img src={tooltipIcon} alt="Tooltip" />
        </Tooltip>
      );
    }
  };

  // To manage scroll to conditional fields
  const conditionalFieldsNames: ConditionalBool[] | undefined =
    currentStepFields
      ?.filter((field) => field.conditional || field.conditionalFile)
      .flatMap((field) => {
        if (field.conditionalFile) {
          return {
            boolField: field.conditionalFile?.name,
            conditionalField: field.name,
            dontScrollTo: Boolean(field.dontScrollTo),
            inputType: field.type,
          };
        } else if (Array.isArray(field.conditional)) {
          return field.conditional.map((conditional) => ({
            boolField: conditional.name,
            conditionalField: field.name,
            dontScrollTo: Boolean(field.dontScrollTo),
            inputType: field.type,
          }));
        }

        return {
          boolField: field.conditional?.name,
          conditionalField: field.name,
          dontScrollTo: Boolean(field.dontScrollTo),
          inputType: field.type,
        };
      });

  const conditionalBoolCurrentField = conditionalFieldsNames?.filter(
    (conditional) => conditional.boolField === name
  );

  const boolFieldsWithSameConditionalField = conditionalFieldsNames?.filter(
    (conditional) => {
      // If the current field is not a conditional field
      if (
        !conditionalBoolCurrentField ||
        conditionalBoolCurrentField.length === 0
      ) {
        return false;
      }

      // Filter the conditional fields that have the same conditional field
      if (conditionalBoolCurrentField[0].boolField === conditional.boolField) {
        return false;
      }

      // Return only the additional conditional fields that have the same conditional field
      return (
        conditional.conditionalField ===
        conditionalBoolCurrentField[0].conditionalField
      );
    }
  );

  const onFileUpload = (file: File) => {
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles, file];

      canUploadMultipleFiles
        ? setFieldValue(name, newFiles)
        : setFieldValue(name, file);

      return newFiles;
    });

    if (conditionalBoolCurrentField) {
      setTimeout(() => {
        scrollToElementName(
          conditionalBoolCurrentField[0]?.conditionalField,
          "file"
        );
      }, 200);
    }
  };

  const onFileRemove = (fileToRemove?: File | FileData) => {
    if (!fileToRemove) throw new Error("File is required to remove it");

    setUploadedFiles((prevFiles) => {
      // Check if the file exists in our current state
      const fileExists = prevFiles.some((file) =>
        compareFiles(file, fileToRemove)
      );

      // If file doesn't exist in parent state, do nothing
      if (!fileExists) {
        return prevFiles;
      }

      // Otherwise, filter out the specific file
      const newFiles = prevFiles.filter(
        (file) => !compareFiles(file, fileToRemove)
      );

      canUploadMultipleFiles
        ? setFieldValue(name, newFiles)
        : setFieldValue(name, null);

      return newFiles;
    });
  };

  useEffect(() => {
    if (disabledByUrlParams) {
      // Field can be disabled by either a string or an array of strings
      if (Array.isArray(disabledByUrlParams)) {
        // Check if any parameter from the array exists in the URL
        const hasAnyParam = disabledByUrlParams.some(
          (param) => searchParams.get(param) !== null
        );
        if (hasAnyParam) setDisabledByUrl(true);
      } else {
        const foundParam = searchParams.get(disabledByUrlParams);
        if (foundParam) setDisabledByUrl(true);
      }
    }
  }, [disabledByUrlParams, searchParams]);

  //Allow to disable the field by a function, by url params or by a boolean
  const getDisabled = () => {
    return (
      (typeof disabled === "boolean" ? disabled : disabled?.(state, values)) ||
      disabledByUrl
    );
  };

  const isInputDisabled: boolean = getDisabled();

  const getInfoText = () => {
    //Info text can be a string or a function that returns a string for dynamic value
    if (infoText === undefined) return undefined;
    return typeof infoText === "string" ? infoText : infoText(value as string);
  };

  if (values && getShouldConditionallyRender()) {
    const inputType = dynamicType ? dynamicType(formik, state) : type;

    switch (inputType) {
      case "date-picker": {
        const minDatePickerDateValue = minDatePickerDate
          ? parseDate(moment(minDatePickerDate).format("YYYY-MM-DD"))
          : undefined;
        const maxDatePickerDateValue = maxDatePickerDate
          ? parseDate(moment(maxDatePickerDate).format("YYYY-MM-DD"))
          : undefined;

        const dateValue = value as string;

        return (
          <ColumnsWrapper columns={columns}>
            <NewDatePicker
              name={name}
              label={getLabel()}
              value={stringToCalendarDate(dateValue) as DateValue}
              onChange={(date) => {
                if (date) {
                  formik.setFieldValue(
                    name,
                    calendarDateToString(date as CalendarDate)
                  );
                } else {
                  formik.setFieldValue(name, null);
                }
              }}
              onBlur={() => {
                setFieldTouched(name, true);
              }}
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              touched={!!touch}
              isDisabled={isInputDisabled}
              minValue={minDatePickerDateValue}
              maxValue={maxDatePickerDateValue}
              showMonthAndYearPickers
            />
          </ColumnsWrapper>
        );
      }
      case "select":
        return (options && options.length > 0) ||
          dynamicOptions ||
          (options && selectType === "single-radio-date-picker-day") ? (
          <ColumnsWrapper id={name} columns={columns}>
            <InputSelect
              key={name}
              label={getLabel()}
              type={selectType || "single"}
              minDatePickerDate={minDatePickerDate}
              maxDatePickerDate={maxDatePickerDate}
              items={
                options && options.length > 0
                  ? options
                  : dynamicOptions
                  ? dynamicOptions(formik, state)
                  : []
              }
              placeholder={placeholder}
              name={name}
              onChange={
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (value: any) => {
                  if (selectType === "single-radio-date-picker-day")
                    return null;

                  if (value.length === 0 && selectType?.includes("multiple")) {
                    setFieldValue(name, null);
                  } else {
                    setFieldValue(name, value);
                  }

                  conditionalResetAndScrollTo({
                    conditionalBoolCurrentField,
                    boolFieldsWithSameConditionalField,
                    values,
                    setFieldValue,
                    scrollToElementName,
                  });

                  if (field?.onChange) {
                    field.onChange(
                      values,
                      setFieldValue,
                      setFieldError,
                      setFieldTouched,
                      value,
                      state,
                      dispatch
                    );
                  }
                }
              }
              onBlur={() => {
                setFieldTouched(name, true);
              }}
              onDatepickerDateChanged={(date) =>
                setFieldValue(name, moment(date[0]).format("YYYY-MM-DD"))
              }
              // If no date picker or
              // If date picker and no value or empty array
              // Assign the value to the input value so it can be reseted
              {...((!selectType?.includes("date-picker") ||
                (selectType?.includes("date-picker") &&
                  (!value ||
                    (Array.isArray(value) && value.length === 0)))) && {
                inputValue: value as string,
              })}
              defaultSelectedKeys={
                defaultSelectedKeys ||
                (typeof initialValue === "string" && initialValue
                  ? [initialValue]
                  : [])
              }
              {...(!!value && {
                initialDatePickerRange: [
                  moment(value as string).toDate(),
                  moment(value as string).toDate(),
                ],
              })}
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              confirmSelection={confirmSelection}
              showExternalBox={!!showExternalBox}
              disabled={isInputDisabled}
              isDisabled={isInputDisabled}
              touched={!!touch}
              classNames={{
                listbox: multilineSelect ? multilineSelectListbox : "",
              }}
              autoComplete="new-password" // To avoid autocomplete in the input (new password is better than off, because some browsers like chrome will still autocomplete the input)
            />
          </ColumnsWrapper>
        ) : null;
      case "radio":
        return options && options.length > 0 ? (
          <ColumnsWrapper columns={columns} className={className}>
            <RadioGroup
              key={name}
              label={getLabel()}
              options={options}
              disabled={isInputDisabled}
              {...getFieldProps(name)}
              onChange={(e) => {
                onChange && onChange(values, setFieldValue);
                formik.handleChange(e);
                conditionalResetAndScrollTo({
                  conditionalBoolCurrentField,
                  boolFieldsWithSameConditionalField,
                  values,
                  setFieldValue,
                  scrollToElementName,
                });
              }}
            />
          </ColumnsWrapper>
        ) : null;
      case "phone":
        return (
          <ColumnsWrapper columns={columns}>
            <InputPhone
              specialLabel={label}
              key={name}
              country={country ?? "ar"}
              infoMessage={getInfoText()}
              {...getFieldProps(name)}
              onChange={(value) => {
                setFieldValue(name, value);
              }}
              errorMessage={error as string}
              touched={!!touch}
              isValid={!!(touch && value && !error)}
              countryCodeEditable={false}
              preferredCountries={["ar", "es", "uy"]}
              label={label}
              dropdownStyle={{ zIndex: 1000 }} // prevent country code dropdown from being hidden by other elements
              containerStyle={{ zIndex: "auto" }}
            />
          </ColumnsWrapper>
        );
      case "file":
        return (
          <ColumnsWrapper columns={columns}>
            <InputFile
              name={name}
              key={name}
              maxSize={maxSize || 0}
              validTypes={validTypes || []}
              multiple={canUploadMultipleFiles}
              text={
                <p>
                  {fileDragTip[language].dragOr}{" "}
                  <span>
                    {canUploadMultipleFiles
                      ? fileDragTip[language].chooseFiles
                      : fileDragTip[language].chooseFile}
                  </span>{" "}
                  {fileDragTip[language].fromDevice}
                </p>
              }
              maxFiles={5}
              isOptional={
                (validation && !isSchemaRequired(validation)) ||
                (conditionalValidation &&
                  !isSchemaRequired(conditionalValidation(state)) &&
                  !conditionalFile)
              }
              tooltip={tooltip ? getTooltip(tooltip) : getConditionalTooltip()}
              anchor={
                anchor && (
                  <div className={anchorStyle} onClick={anchor.onClick}>
                    <p>{anchor.text}</p>
                  </div>
                )
              }
              className={fileStyle}
              label={label}
              infoText={infoText as string}
              infoTextPosition={infoTextPosition}
              onFileUpload={onFileUpload}
              sizeErrorMessage={inputFile[language].sizeErrorMessage}
              formatErrorMessage={inputFile[language].formatErrorMessage}
              replaceImageText={inputFile[language].replaceImageText}
              attachImageText={inputFile[language].attachImageText}
              hideDownloadIcon={
                value ? isEmptyFile(value as SerializableFile) : false
              }
              onFileRemove={onFileRemove}
              value={value as File}
            />
            {typeof error === "string" &&
              !!value &&
              error === "MIN-SIZE-ERROR" && (
                <Message
                  message={`${inputFile[language].minSizeErrorMessage} ${
                    minFileSize * 1000
                  } KB`}
                  variant="error"
                />
              )}
          </ColumnsWrapper>
        );
      case "token":
        return (
          <ColumnsWrapper columns={columns}>
            <p key={name}>Add InputToken</p>
          </ColumnsWrapper>
        );
      case "multiple-radio":
        return segmentedOptions && segmentedOptions.length > 0 ? (
          <ColumnsWrapper columns={columns}>
            <SegmentedOptionsInput
              key={name}
              label={label}
              options={segmentedOptions}
              isInvalid={!touched && !value}
              {...getFieldProps(name)}
            />
          </ColumnsWrapper>
        ) : null;
      case "multiple-checkbox":
        return options && options.length > 0 ? (
          <ColumnsWrapper columns={columns}>
            <CheckboxGroup
              classNames={{ wrapper: checkboxGroupWrapper }}
              key={name}
              label={getLabel()}
              children={options.map((option) => (
                <Checkbox
                  key={option.value}
                  value={option.value}
                  onValueChange={(isSelected: boolean) =>
                    setFieldValue(
                      name,
                      isSelected
                        ? [...((value as string[]) ?? []), option.value]
                        : ((value as string[]) ?? []).filter(
                            (selection: string) => selection !== option.value
                          )
                    )
                  }
                >
                  {option.label}
                </Checkbox>
              ))}
              {...getFieldProps(name)}
              defaultValue={[]}
              onChange={() => {
                onChange && onChange(values, setFieldValue);
                conditionalResetAndScrollTo({
                  conditionalBoolCurrentField,
                  boolFieldsWithSameConditionalField,
                  values,
                  setFieldValue,
                  scrollToElementName,
                });
              }}
            />
          </ColumnsWrapper>
        ) : null;
      case "textarea":
        return (
          <ColumnsWrapper columns={columns}>
            <InputTextArea
              key={name}
              label={label}
              type={inputType}
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              isDisabled={isInputDisabled}
              placeholder={placeholder}
              maxLength={maxLength}
              {...getFieldProps(name)}
            />
          </ColumnsWrapper>
        );
      case "address": {
        return (
          <ColumnsWrapper columns={columns}>
            <InputAddress
              {...getFieldProps(name)}
              onBlur={(e) => {
                handleBlur(e);
              }}
              key={name}
              label={label}
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              disabled={isInputDisabled}
              placeholder={placeholder}
              value={(value as string) ?? ""}
              onValueChange={async (address) => {
                if (!address) {
                  if (touch) setFieldValue(name, "");
                  else {
                    if (autoSelect) setFieldValue(name, "");
                    else setFieldValue(name, value);
                  }
                  return;
                }
                onChange && onChange(address, setFieldValue);
              }}
              country={country && (getNestedValue(values, country) as string)}
              autoSelect={
                autoSelect && (getNestedValue(values, autoSelect) as string)
              }
              autoComplete={autoComplete}
              onChange={formik.handleChange}
              touched={!!touch}
            />
          </ColumnsWrapper>
        );
      }
      case "dni":
      case "cuit":
      case "text":
      case "email":
      case "number":
      case "date":
      case "money":
        return (
          <ColumnsWrapper columns={columns} key={name}>
            {tooltip && tooltip.external && (
              <div
                className={externalTooltip}
                onClick={() => dispatch(openModal({ name: tooltip.modal }))}
              >
                {tooltip.label}
              </div>
            )}
            <Input
              {...getFieldProps(name)}
              label={getLabel()}
              type={
                inputType === "date"
                  ? "text"
                  : (inputType as InputProps["type"])
              }
              isInvalid={!!error && !!touch}
              errorMessage={error as string}
              isDisabled={isInputDisabled}
              placeholder={placeholder}
              initialValue={
                typeof initialValue === "string" && initialValue
                  ? initialValue
                  : (value as string)
              }
              isNumberPercentage={isNumberPercentage}
              maxLength={maxLength}
              infoMessage={getInfoText()}
              touched={!!touch}
              {...getFieldProps(name)}
              value={value as string}
              onKeyDown={(e) => {
                if (inputType === "date") {
                  handleDateInputKeyDown(e, value as string);
                }
              }}
              onChange={(element) => {
                formik.handleChange(element);

                if (inputType === "date")
                  handleDateFieldValueChanged(
                    element.target.value,
                    (newValue: string) => {
                      setFieldValue(name, newValue);
                    }
                  );

                return onChange?.(
                  values,
                  setFieldValue,
                  setFieldError,
                  setFieldTouched,
                  element
                );
              }}
              onBlur={(e) => {
                handleBlur(e);
                onBlur &&
                  onBlur(
                    values,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                    e,
                    state
                  );
              }}
              {...(tooltip &&
                !tooltip?.external && {
                  info: "CUSTOM",
                  customInfo:
                    tooltip.label &&
                    (tooltip.content ? (
                      <Tooltip
                        placement="top"
                        content={tooltip.content}
                        width={300}
                      >
                        <p className={labelInfo}>{tooltip.label}</p>
                      </Tooltip>
                    ) : (
                      <p
                        onClick={() =>
                          dispatch(openModal({ name: tooltip.modal }))
                        }
                        className={labelInfo}
                      >
                        {tooltip.label}
                      </p>
                    )),
                })}
            />
          </ColumnsWrapper>
        );
      case "checkbox":
        return (
          <ColumnsWrapper columns={columns} key={name}>
            <Checkbox
              {...getFieldProps(name)}
              isSelected={value === true || (value !== false && !!initialValue)}
              classNames={{
                wrapper: checkboxWrapper,
              }}
              onChange={(e: string | React.ChangeEvent) => {
                onChange &&
                  onChange(
                    values,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched
                  );
                formik.handleChange(e);
              }}
            >
              <div className={checkboxLabel} id={`label-${name}`}>
                {label}
              </div>
            </Checkbox>
          </ColumnsWrapper>
        );

      case "button":
        return (
          <ColumnsWrapper columns={columns} key={name}>
            <Button
              text={buttonProps?.text || ""}
              {...(buttonProps
                ? {
                    type: buttonProps.type,
                    variant: buttonProps.variant,
                    startContent: buttonProps.startContent,
                    onClick: () => {
                      if (buttonProps?.onClick) {
                        buttonProps.onClick(values, dispatch, state);
                      }
                    },
                    disabled: buttonProps.disabled
                      ? buttonProps.disabled(values, state)
                      : false,
                  }
                : {})}
            />
          </ColumnsWrapper>
        );
      case "label":
        return (
          <ColumnsWrapper columns={columns} key={name}>
            {text}
          </ColumnsWrapper>
        );
      case "alert":
        return alertContent && alertContent();
    }
  } else {
    return null;
  }
};

export default TemplateInputSelector;
